import { useState } from "react";
import { APP_STATUS, type ApplicationStatus, type GigApplication } from "../../api/gigboard";
import { Banner } from "../../components/Banner";
import { Button } from "../../components/Button";
import { agoText } from "./gigFormat";
import { GigModal, ModalKicker, ModalTitle } from "./GigModal";
import { WinFields, emptyWin, winError, type WinDraft } from "./WinFields";

export interface CheckinResult {
  answers: { applicationId: string; status: ApplicationStatus }[];
  otherWork: boolean;
  win: WinDraft | null;
}

/**
 * The Friday check-in: a short, must-answer pop-up. Question 1 (only when there are applications to ask about) is one
 * tap per application; question 2 asks about any other paid work, with the win form inline on "Yes".
 */
export function CheckinModal({
  firstName,
  applications,
  busy,
  error,
  onFinish,
  onAddDetails,
  onDone,
}: {
  firstName: string;
  applications: GigApplication[];
  busy: boolean;
  error: string | null;
  /** Resolves true once the server has saved the check-in. */
  onFinish: (result: CheckinResult) => Promise<boolean>;
  onAddDetails: (application: GigApplication) => void;
  onDone: () => void;
}) {
  const first = firstName || "there";
  const n = applications.length;
  const [step, setStep] = useState<1 | 2 | 3>(n ? 1 : 2);
  const [answers, setAnswers] = useState<Record<string, ApplicationStatus>>({});
  const [other, setOther] = useState<"yes" | "no" | null>(null);
  const [win, setWin] = useState<WinDraft>(() => emptyWin());
  const [problem, setProblem] = useState<string | null>(null);

  const stepLabel =
    n && step < 3 ? (
      <div className="mb-1.5 text-[13px] text-text-muted">Question {step} of 2</div>
    ) : null;

  const finish = async () => {
    if (other === "yes") {
      const invalid = winError(win);
      setProblem(invalid);
      if (invalid) return;
    }
    const saved = await onFinish({
      answers: applications.flatMap((a) =>
        answers[a.id] ? [{ applicationId: a.id, status: answers[a.id]! }] : [],
      ),
      otherWork: other === "yes",
      win: other === "yes" ? win : null,
    });
    if (saved) setStep(3);
  };

  let body;
  if (step === 1) {
    body = (
      <>
        <ModalTitle>Hi {first}, how did your applications go?</ModalTitle>
        {stepLabel}
        <p className="text-[13px] text-text-muted">
          One tap each. This helps us see what's working for participants.
        </p>
        {applications.map((app) => (
          <div key={app.id} className="border-b border-border py-3 last:border-b-0">
            <b>{app.title}</b>
            <div className="text-[13px] text-text-muted">
              {app.source} · you looked at this {agoText(app.clickedAt).toLowerCase()}
            </div>
            <div
              role="group"
              aria-label={`How did ${app.title} go?`}
              className="mt-2 flex flex-wrap gap-1.5"
            >
              {APP_STATUS.map(([status, label]) => {
                const on = answers[app.id] === status;
                return (
                  <button
                    key={status}
                    type="button"
                    aria-pressed={on}
                    onClick={() => setAnswers((current) => ({ ...current, [app.id]: status }))}
                    className={`min-h-9 rounded-[var(--radius-chip)] border-[1.5px] px-3 text-[13px] font-semibold ${
                      on
                        ? "border-accent bg-accent text-on-accent"
                        : "border-border bg-surface text-text hover:border-accent"
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        <div className="mt-3 flex flex-wrap gap-2.5">
          <Button disabled={!applications.every((a) => answers[a.id])} onClick={() => setStep(2)}>
            Next →
          </Button>
        </div>
      </>
    );
  } else if (step === 2) {
    body = (
      <>
        <ModalTitle>
          {n
            ? "Any other paid work this week?"
            : `Hi ${first}, did you do any paid work this week?`}
        </ModalTitle>
        {stepLabel}
        <p className="text-[13px] text-text-muted">
          From anywhere: a gig platform, a friend's recommendation, social media, a past client, a
          job or an internship. If you were paid for using your skill, it counts.
        </p>
        <div className="flex flex-wrap gap-2.5">
          <Button
            variant={other === "yes" ? "primary" : "ghost"}
            aria-pressed={other === "yes"}
            onClick={() => setOther("yes")}
          >
            Yes
          </Button>
          <Button
            variant={other === "no" ? "primary" : "ghost"}
            aria-pressed={other === "no"}
            onClick={() => {
              setOther("no");
              setProblem(null);
            }}
          >
            No, nothing new
          </Button>
        </div>
        {other === "yes" ? (
          <div className="mt-3 rounded-[var(--radius-card)] border border-border p-4">
            <WinFields value={win} onChange={setWin} />
          </div>
        ) : null}
        {problem || error ? <Banner tone="bad">{problem ?? error}</Banner> : null}
        <div className="mt-3 flex flex-wrap gap-2.5">
          {n ? (
            <Button variant="ghost" onClick={() => setStep(1)}>
              ← Back
            </Button>
          ) : null}
          <Button disabled={!other || busy} onClick={() => void finish()}>
            {busy ? "Saving..." : "Finish check-in"}
          </Button>
        </div>
      </>
    );
  } else {
    const wonApps = applications.filter((a) => answers[a.id] === "won");
    const won = wonApps.length > 0 || other === "yes";
    body = (
      <>
        <ModalTitle>{won ? "Congratulations!" : `Thanks, ${first}.`}</ModalTitle>
        <p>
          {won
            ? "Paid work using your skill is exactly what this program is for."
            : "Keep applying: the Gig Board has fresh listings every day. See you next Friday."}
        </p>
        {wonApps.length ? (
          <>
            <p>
              <b>One last thing:</b> tell us about the gig{wonApps.length > 1 ? "s" : ""} you got,
              so it counts toward your results.
            </p>
            <div className="flex flex-wrap gap-2.5">
              {wonApps.map((app) => (
                <Button key={app.id} onClick={() => onAddDetails(app)}>
                  Add details: {app.title.slice(0, 40)}
                </Button>
              ))}
            </div>
          </>
        ) : null}
        <div className="mt-3 flex flex-wrap gap-2.5">
          <Button variant={wonApps.length ? "ghost" : "primary"} onClick={onDone}>
            {wonApps.length ? "Later" : "Done"}
          </Button>
        </div>
      </>
    );
  }

  return (
    <GigModal label="Friday check-in" locked>
      <ModalKicker>Friday check-in</ModalKicker>
      {body}
    </GigModal>
  );
}
